<template>
  <Card class="p-2.5 rounded-2xl" :style="cardStyle">
    <div class="card-body">
      <div>
        <slot name="giftcard-buy-box">
          <div class="flex justify-between items-center mb-2">
            <EventBuyBoxPrice
              v-if="displayPrice"
              :selected-ticket-quantity="1"
              :kft-event="kftEvent"
              :loading-pricings="loadingPricings"
              :type="type"
              is-giftcard
              class=""
              :label="
                shouldDisplayBuyBox
                  ? $t(`product.show.checkout.giftcard.sectionTitle`)
                  : ''
              "
              :special-price-label="
                $t('product.show.checkout.giftcard.discountLabel')
              "
            >
              <template
                #main-price="{ label, totalPriceToDisplay, loadingPricings }"
              >
                <div class="main-price">
                  <span v-if="label">{{ label }}</span>

                  <!-- loader -->
                  <div
                    class="h-[28px] rounded-md"
                    :class="{
                      'w-20 bg-gray-200 animate-pulse':
                        loadingPricings || !totalPriceToDisplay,
                    }"
                  >
                    <template v-if="!loadingPricings && totalPriceToDisplay">
                      <span v-if="totalPriceToDisplay">{{
                        totalPriceToDisplay
                      }}</span>
                    </template>
                  </div>
                </div>
              </template>
            </EventBuyBoxPrice>
            <div
              v-if="isRequestButtonsVisible && !isSpecialGiftcardEvent"
              class="flex gap-2 p-1"
            >
              <client-only>
                <EventBuyBoxWatilistButton />
                <EventBuyBoxRequestDateButton
                  v-if="showDateRequestButton"
                  class="px-2 py-1"
                  request-location="buybox_giftcard"
                />
              </client-only>
            </div>
          </div>
          <InfoBanner
            v-if="isRequestButtonsVisible && !isSpecialGiftcardEvent"
            class="mb-2"
            :info-text="$t(`product.show.checkout.giftcard.infoBannerText`)"
          />
          <LazyEventBuyBoxGiftcard
            v-if="giftcardPricings"
            :pricings="giftcardPricings"
            is-primary-button
            :enable-embedded="enableEmbedded"
            :enable-subdomain="enableSubdomain"
            :text-color="textColor"
            :cta-background-color="backgroundColor"
            @add-item-to-cart="emit('add-item-to-cart')"
          />
        </slot>
      </div>
    </div>
  </Card>
</template>

<script lang="ts" setup>
import type { EventDateType, EventDescription } from '~/composables/types/event'
import { useIsSubdomain } from '~/composables/useSubdomain'

const { t: $t } = useI18n()

const {
  enableEmbedded = false,
  displayPrice = true,
  shouldDisplayBuyBox = false,
  type = 'PUBLIC',
  textColor = undefined,
  backgroundColor = undefined,
  cardBackgroundColor = undefined,
} = defineProps<{
  enableEmbedded?: boolean
  type: EventDateType
  displayPrice?: boolean
  shouldDisplayBuyBox?: boolean
  isSpecialGiftcardEvent?: boolean
  textColor?: string
  backgroundColor?: string
  cardBackgroundColor?: string
}>()

const cardStyle = computed(() => {
  return {
    ...(cardBackgroundColor && { backgroundColor: cardBackgroundColor }),
  }
})

const emit = defineEmits<{
  (e: 'add-item-to-cart'): void
}>()

const eventData = inject('eventData') as EventDescription

const { getInitialCategoryQuantities } = useTicketQuantityCalculations()
const ticketCategoriesArray = computed<PricingCategory[]>(
  () => eventData?.pricing?.data?.pricingCategories?.data || []
)
const selectedCategoryQuantities = ref<Record<string, number>>(
  getInitialCategoryQuantities(ticketCategoriesArray.value)
)

const {
  fetchPricings,
  pricings: pricingsData,
  loading: loadingPricings,
} = useEventPricings(ref(eventData) as Ref<EventDescription | undefined>, {
  dateId: ref(undefined),
  quantity: ref(1),
  isPrivate: ref(false),
  type: ref('GIFT_CARD'),
  categoryQuantities: selectedCategoryQuantities,
})

provide('pricingsData', pricingsData)

const giftcardPricings = computed(() => Object.values(pricingsData.value)[0])

const eventBuyBoxVisibleElements = inject('eventBuyBoxVisibleElements') as
  | {
      requestButtons?: boolean
    }
  | undefined
const isRequestButtonsVisible = computed(
  () =>
    (eventBuyBoxVisibleElements?.requestButtons ?? true) && !shouldDisplayBuyBox
)

const kftEvent = inject<EventDescription>('eventData')!
const enableSubdomain = useIsSubdomain()
const showDateRequestButton = computed(
  () =>
    eventGetters.isDateRequestActiveBS(kftEvent, enableSubdomain.value) ||
    eventGetters.isInstantBookingActive(kftEvent)
)

onMounted(async () => {
  await fetchPricings()
})
</script>

<style lang="postcss" scoped>
.main-price {
  @apply text-stone-700 text-lg font-medium text-nowrap flex gap-2 items-center;
  @apply min-h-[28px];
}
</style>
